<script setup>
import { ref, defineProps, defineEmits, computed } from "vue";
import { apiQuery } from "@/api/j24api";
import { patch24Store } from "@/stores/patch24";
const patch24 = patch24Store();

const props = defineProps({
  commands: Array,
  appsList: Array,
  loading: Boolean,
  readOnlyMode: Boolean,
});
const emit = defineEmits(["UpdateCommands"]);
const selectedCommand = ref(null);
const showConfirmDelete = ref(false);
const errorOverlay = ref(false);
const loading = ref(false);

const headers = computed(() => {
  var result = [];
  if (props.appsList?.length) {
    result.push({ title: "App Name", value: "appName" });
  }
  result.push(
    { title: "Version", value: "version" },
    { title: "Type", value: "type" },
    { title: "Scope", value: "scope" },
    { title: "Created", value: "created" },
    { title: "Created By", value: "createdBy" },
    { title: "Actions", value: "actions" }
  );
  if (props.readOnlyMode) {
    // Remove Actions column
    result = result.filter((h) => h.value !== "actions");
  }
  console.log("Computed headers:", result);
  return result;
});

const formattedCommands = computed(() => {
  return props.commands.map((cmd) => {
    let scope = "Global";
    if (cmd.serial) {
      scope = "Device " + cmd.serial;
    } else if (cmd.scope == "configuration") {
      scope = "Configuration " + cmd.groupID;
    }
    return {
      ...cmd,
      scope: scope,
      appName: getAppName(cmd.appId),
      created: new Date(cmd.created).toLocaleString(),
    };
  });
});

function getAppName(appId) {
  const app = props.appsList.find((app) => app.id === appId);
  return app ? app.name : "Unknown App";
}

function deleteCommand(item) {
  selectedCommand.value = item;
  showConfirmDelete.value = true;
}

async function deleteCommandConfirmed(item) {
  // Call API to delete command
  loading.value = true;
  showConfirmDelete.value = false;
  selectedCommand.value = null;
  var body = {
    action: "cancelCommand",
    licenseId: patch24.selectedLicense._id,
  };
  if (item.serial) {
    body.id = item._id;
    body.serial = item.serial;
  } else if (item.scope == "Global") {
    body.uuid = item.uuid;
  } else if (item.groupID) {
    body.groupID = item.groupID;
    body.uuid = item.uuid;
  }
  try {
    let result = await apiQuery("ELA", "patching/patchingManagement", body);
    emit("UpdateCommands");
  } catch (error) {
    console.error("Error cancelling command:", error);
    errorOverlay.value = true;
  }
  loading.value = false;
  showConfirmDelete.value = false;
  selectedCommand.value = null;
}
</script>

<template>
  <v-data-table
    :headers="headers"
    :items="formattedCommands"
    :loading="props.loading"
    no-data-text="No Commands"
    loading-text="Loading Commands..."
  >
    <template v-slot:[`item.actions`]="{ item }">
      <v-icon class="pointer" @click="deleteCommand(item)" color="primary"
        >mdi-delete</v-icon
      >
    </template>
  </v-data-table>

  <v-dialog v-model="showConfirmDelete" max-width="400">
    <v-card>
      <v-card-title class="headline">Cancel Command?</v-card-title>
      <v-card-text>
        <div class="mb-2">Are you sure you want to cancel this command?</div>
        <div v-if="selectedCommand">
          <div v-if="selectedCommand.serial">
            <strong>Serial:</strong> {{ selectedCommand.serial }}
          </div>
          <div><strong>App ID:</strong> {{ selectedCommand.appId }}</div>
          <div><strong>Type:</strong> {{ selectedCommand.type }}</div>
          <div><strong>Version:</strong> {{ selectedCommand.version }}</div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn @click="showConfirmDelete = false" variant="elevated"
          >Cancel</v-btn
        >
        <v-btn
          color="primary"
          @click="deleteCommandConfirmed(selectedCommand)"
          variant="elevated"
          :loading="loading"
          >Delete</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="errorOverlay" max-width="400">
    <v-card>
      <v-card-title class="headline">Error deleting command</v-card-title>
      <v-card-text>
        There was an error deleting the command. Please try again.
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn color="grey" variant="text" @click="errorOverlay = false"
          >OK</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
